<script setup>
import { ref, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import ContactAPI from 'dashboard/api/contacts';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['updated']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const dialogRef = ref(null);
const contactId = ref(null);
const isSaving = ref(false);
const conversations = ref([]);

const form = reactive({ name: '', email: '', phoneNumber: '' });

const STATUS_CLASS = {
  open: 'text-n-teal-11 bg-n-teal-3',
  pending: 'text-n-amber-11 bg-n-amber-3',
  snoozed: 'text-n-slate-11 bg-n-alpha-2',
  resolved: 'text-n-slate-11 bg-n-alpha-2',
};

const loadConversations = async id => {
  try {
    const { data } = await ContactAPI.getConversations(id);
    conversations.value = data.payload || [];
  } catch {
    conversations.value = [];
  }
};

const openForContact = async contact => {
  contactId.value = contact.id;
  form.name = contact.name || '';
  form.email = contact.email || '';
  form.phoneNumber = contact.phone_number || '';
  conversations.value = [];
  dialogRef.value?.open();
  // Refresh with the latest data + conversations.
  try {
    const { data } = await ContactAPI.show(contact.id);
    const fresh = data.payload || {};
    form.name = fresh.name || '';
    form.email = fresh.email || '';
    form.phoneNumber = fresh.phone_number || '';
  } catch {
    // keep the values we already have
  }
  loadConversations(contact.id);
};

const save = async () => {
  isSaving.value = true;
  try {
    const { data } = await ContactAPI.update(contactId.value, {
      name: form.name.trim(),
      email: form.email.trim(),
      phone_number: form.phoneNumber.trim(),
    });
    emit('updated', data.payload);
    useAlert(t('KANBAN.CONTACT.SAVED'));
    dialogRef.value?.close();
  } catch {
    useAlert(t('KANBAN.CONTACT.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const jumpToConversation = id => {
  const path = frontendURL(
    conversationUrl({ accountId: route.params.accountId, id })
  );
  dialogRef.value?.close();
  router.push({ path });
};

const KNOWN_STATUSES = ['open', 'resolved', 'pending', 'snoozed'];
const statusLabel = status =>
  KNOWN_STATUSES.includes(status)
    ? t(`KANBAN.CONTACT.STATUS.${status}`)
    : status || '';
const statusClass = status => STATUS_CLASS[status] || STATUS_CLASS.resolved;

defineExpose({ openForContact });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('KANBAN.CONTACT.TITLE')"
    :confirm-button-label="t('KANBAN.CONTACT.SAVE')"
    :is-loading="isSaving"
    @confirm="save"
  >
    <div class="flex flex-col gap-4">
      <Input v-model="form.name" :label="t('KANBAN.CONTACT.NAME')" />
      <Input v-model="form.email" :label="t('KANBAN.CONTACT.EMAIL')" />
      <Input v-model="form.phoneNumber" :label="t('KANBAN.CONTACT.PHONE')" />

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.CONTACT.CONVERSATIONS') }}
        </span>
        <div class="flex flex-col gap-1">
          <button
            v-for="conv in conversations"
            :key="conv.id"
            type="button"
            class="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-n-weak hover:border-n-strong"
            @click="jumpToConversation(conv.id)"
          >
            <span class="flex items-center gap-2 min-w-0">
              <span class="text-sm text-n-slate-12">#{{ conv.id }}</span>
              <span
                class="text-[11px] px-1.5 py-0.5 rounded-md font-medium"
                :class="statusClass(conv.status)"
              >
                {{ statusLabel(conv.status) }}
              </span>
            </span>
            <span class="inline-flex items-center gap-1 text-xs text-n-brand">
              {{ t('KANBAN.CONTACT.JUMP') }}
              <span class="i-lucide-arrow-up-right size-3.5" />
            </span>
          </button>
          <span v-if="!conversations.length" class="text-xs text-n-slate-10">
            {{ t('KANBAN.CONTACT.NO_CONVERSATIONS') }}
          </span>
        </div>
      </div>
    </div>
  </Dialog>
</template>
